import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  ArrowRight, 
  Shield, 
  FileText, 
  Clock,
  Sparkles,
  Loader2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Sources officielles avec emojis
const OFFICIAL_SOURCES = [
  { name: "Impôts", emoji: "🏛️" },
  { name: "Ameli", emoji: "💚" },
  { name: "URSSAF", emoji: "📋" },
  { name: "EDF", emoji: "⚡" },
  { name: "Banques", emoji: "🏦" },
];

const BENEFITS = [
  {
    icon: FileText,
    title: "Avis d'imposition, relevés, attestations...",
    description: "Tous vos documents officiels au même endroit",
  },
  {
    icon: Clock,
    title: "Import automatique",
    description: "Plus besoin de chercher ou scanner vos documents",
  },
  {
    icon: Sparkles,
    title: "Classement intelligent",
    description: "L'IA organise tout pour vous instantanément",
  },
];

interface EasySyncOnboardingCardProps {
  onConnect: () => void;
  loading?: boolean;
}

export function EasySyncOnboardingCard({ onConnect, loading }: EasySyncOnboardingCardProps) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <>
      <Card 
        className="cursor-pointer border-border hover:border-primary/40 hover:shadow-sm transition-all duration-200 group"
        onClick={() => setShowDetails(true)}
      >
        <CardContent className="p-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/10 to-primary/20 border border-primary/20 flex items-center justify-center group-hover:scale-105 transition-transform shadow-sm">
            <Building2 className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm flex items-center gap-2">
              Documents officiels
              <Badge variant="secondary" className="text-[8px] px-1.5 py-0 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-0">
                Nouveau
              </Badge>
            </div>
            <div className="text-[10px] text-muted-foreground">Impôts, Ameli, URSSAF, banques et +50 sources</div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/25">
                <Building2 className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <span>Documents officiels</span>
                <DialogDescription className="text-left font-normal">
                  Import automatique depuis vos organismes
                </DialogDescription>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Sources preview */}
            <div className="flex flex-wrap gap-2">
              {OFFICIAL_SOURCES.map((source) => (
                <div
                  key={source.name}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary/50 border border-border/50"
                >
                  <span className="text-sm">{source.emoji}</span>
                  <span className="text-xs font-medium text-muted-foreground">{source.name}</span>
                </div>
              ))}
              <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-secondary/30 border border-dashed border-border/50">
                <span className="text-xs text-muted-foreground">+50 sources</span>
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-3">
              {BENEFITS.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <benefit.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{benefit.title}</p>
                    <p className="text-xs text-muted-foreground">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Security note */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              <Shield className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <p className="text-xs text-emerald-700 dark:text-emerald-400">
                Connexion sécurisée via un partenaire agréé par l'ACPR. Vos identifiants ne sont jamais stockés par EasyFinder.
              </p>
            </div>

            {/* CTA */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowDetails(false)}
              >
                Plus tard
              </Button>
              <Button
                className="flex-1 gap-2"
                onClick={() => {
                  setShowDetails(false);
                  onConnect();
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Activer l'import
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
